import React from 'react';
import Typography from '../../../StyledTypography';
import { getUserName } from '../../utils/userUtils';
import UserSelectionBase from './UserSelectionBase';
import { useTranslation } from 'react-i18next';
/**
 * Head Selection Component - Handles different head selection logic based on node level
 * @param {Object} node - Current node: { id, level, name, head, ... }
 * @param {Array} selectedHeads - Level 1 selected heads: [{ id, username }, ...]
 * @param {Object} selectedSingleHead - Level 2 selected head: { id, username } or null
 * @param {Array} filteredHeads - Level 1 filtered heads: [{ id, username }, ...]
 * @param {Array} filteredSingleHeads - Level 2 filtered heads: [{ id, username }, ...]
 * @param {Function} searchHeads - Level 1 search function: (event) => void
 * @param {Function} searchSingleHeads - Level 2 search function: (event) => void
 * @param {Function} onHeadChange - Level 1 change handler: (event) => void
 * @param {Function} onSingleHeadChange - Level 2 change handler: (event) => void
 * @param {Object} errors - Validation errors: { head: 'error message' }
 * @param {Array} users - All users: [{ id, username }, ...]
 * @param {boolean} disabled - Whether to disable the field
 * @param {boolean} disableInviteUsers - Whether to disable adding users emails invitations for external users without privileges
 * @returns {JSX.Element} - Different UI based on node level and head configuration
 *
 * Logic:
 * - Level 1: Single-select head with validation (required)
 * - Level 2 with 1 head: Auto-selected, displayed as static text (inherited from parent)
 * - Level 2 with multiple heads: Single-select from parent heads
 * - Level 2 with no heads: Static display "Not assigned"
 * - Level 3+: Static display only
 */
export default function HeadSelection({
  node,
  selectedHeads,
  selectedSingleHead,
  filteredHeads,
  filteredSingleHeads,
  searchHeads,
  searchSingleHeads,
  onHeadChange,
  onSingleHeadChange,
  errors,
  users,
  disableInviteUsers = false,
  disabled = false,
}) {
  const { t } = useTranslation();
  // Level 1: Single-select head (required field with validation)
  if (node.level === 1) {
    return (
      <UserSelectionBase
        label={t('department_head')}
        required
        value={selectedSingleHead}
        suggestions={filteredHeads}
        completeMethod={searchHeads}
        onChange={onSingleHeadChange}
        placeholder={
          disableInviteUsers ? t('search_user') : t('search_user_or_type_email')
        }
        errors={errors?.head}
        enableKeyboardAdd={!disableInviteUsers}
        disabled={disabled}
      />
    );
  }
  // Level 2: Head selection depends on parent node configuration
  else if (node.level === 2) {
    console.log('level 2 head -> ', node.head);

    // Case 1: Single head inherited from parent - display as static text (inherited)
    if (node?.head && Array.isArray(node.head) && node.head.length === 1) {
      const headName = selectedSingleHead
        ? typeof selectedSingleHead === 'string'
          ? selectedSingleHead
          : selectedSingleHead.username
        : getUserName(node.head[0], users) || t('not_assigned');

      return (
        <div className="form-field-container">
          <Typography variant="bodyDefault" className="department-head-label">
            {t('department_head')}
            <span className="department-head-inherited-note">
              {t('inherited_from_parent')}
            </span>
          </Typography>
          <Typography variant="bodySmall" className="department-head-value">
            {headName}
          </Typography>
        </div>
      );
    }
    // Case 2: Multiple heads from parent - allow selection of one
    else if (node?.head && Array.isArray(node.head) && node.head.length > 1) {
      return (
        <UserSelectionBase
          label={t('department_head')}
          value={selectedSingleHead}
          suggestions={filteredSingleHeads}
          completeMethod={searchSingleHeads}
          onChange={onSingleHeadChange}
          placeholder={t('search_user_or_type_email')}
        />
      );
    }
    // Case 3: No heads available from parent - display static text
    else {
      return (
        <div className="form-field-container">
          <Typography variant="bodyDefault" className="department-head-label">
            {t('department_head')}
          </Typography>
          <Typography variant="bodySmall" className="department-head-value">
            {node?.head ? getUserName(node.head, users) : t('not_assigned')}
          </Typography>
        </div>
      );
    }
  }
  // Level 3+: Static display only (no editing allowed)
  else {
    return (
      <div className="form-field-container">
        <Typography variant="bodyDefault" className="department-head-label">
          {t('department_head')}
        </Typography>
        <Typography variant="bodySmall" className="department-head-value">
          {node?.head ? getUserName(node.head, users) : t('not_assigned')}
        </Typography>
      </div>
    );
  }
}
